import type { ComponentType } from 'react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pressable, StyleSheet, Text, View, useSafeAreaInsets } from '@/ui';
import { ActivityIcon, BagIcon, HomeIcon, UserIcon, UsersIcon, MoreIcon } from './icons';
import { tokens } from '@/theme/tokens';
import { useAppTheme, type ThemeColors } from '@/theme/ThemeProvider';

type IconCmp = ComponentType<{ size?: number; color?: string }>;

export type TabRoute = { name: string; path: string; title: string };

// Icons per tab route; labels come from each tab's translated title.
const TABS: Record<string, { Icon: IconCmp }> = {
  home: { Icon: HomeIcon },
  match: { Icon: ActivityIcon },
  shop: { Icon: BagIcon },
  community: { Icon: UsersIcon },
  me: { Icon: UserIcon },
  more: { Icon: MoreIcon },
};

const ACTIVE = tokens.accentMint;

/**
 * Custom bottom tab bar matching the prototype `.tabbar` (mint active state).
 *
 * Mobile drove this off react-navigation's tab state; here the active tab is
 * the URL, and each tab is a `replace` rather than a `push` so tabbing around
 * does not build a history stack the back button then has to walk out of.
 */
export function SagesseTabBar({ tabs }: { tabs: TabRoute[] }) {
  const insets = useSafeAreaInsets();
  const { colors: C } = useAppTheme();
  const styles = useMemo(() => createStyles(C), [C]);
  const inactive = C.textFaint;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <View
      style={[styles.bar, { paddingBottom: Math.max(insets.bottom, 6) }]}
      accessibilityRole="navigation"
    >
      {tabs.map((route) => {
        const tab = TABS[route.name];
        if (!tab) return null; // ignore anything that isn't one of the six tabs

        const focused = pathname === route.path || pathname.startsWith(route.path + '/');
        const color = focused ? ACTIVE : inactive;
        const { Icon } = tab;

        return (
          <Pressable
            key={route.name}
            onPress={() => {
              if (!focused) navigate(route.path, { replace: true });
            }}
            style={styles.item}
            accessibilityRole="link"
            accessibilityLabel={route.title}
            accessibilityState={{ selected: focused }}
          >
            <Icon size={22} color={color} />
            <Text style={[styles.label, { color }]} numberOfLines={1}>
              {route.title}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function createStyles(C: ThemeColors) {
  return StyleSheet.create({
    bar: {
      flexDirection: 'row',
      backgroundColor: C.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: C.border,
      paddingTop: 8,
      paddingHorizontal: 6,
    },
    item: { flex: 1, alignItems: 'center', gap: 3, paddingVertical: 2 },
    label: { fontSize: 10, fontWeight: '600' },
  });
}
